import {
    useEffect,
    useState } from "react";
import { useSelector } from "react-redux";
import useUiController from "../../global/hooks/useUiController";
import SnpBaseLayout from "../../global/SnpBaseLayout";
import "./OnlineWatchBill.css"
import {
  getAuthSession,
  INITIATED,
  printDebugLog,
  SUCCESS,
  TRIP_ASSIGNED,
  TRIP_RUNNING
} from "../../../constants/constant";
import { snpAxios } from "../../global/api";
import BillListDisplay from "../common/BillListDisplay";
import DialogBox from "../../global/DialogBox";
import DeliveryStatusChange from "../common/DeliveryStatusChange";
import SnpButton from "../../global/SnpButton";
import DismissButton from "../../global/DismissButton";
import { useNavigate } from "react-router-dom";

const OnlineWatchBill = () => {

    const storeActiveShopHash = useSelector(state => state.setActiveShopHashReducer.activeShopHash);
    const storeApiUrl = useSelector(state => state.setApiUrlReducer.MAINURL.INITIAL_BASE_URL);
    const navigate = useNavigate();

    const {
        loader,
        setLoader,
        snackbarConfig,
        showSnackbar,
        closeSnackbar,
        dialogConfig,
    } = useUiController();

    const [initiatedBills, setInitiatedBills] = useState([]);
    const [assignedBills, setAssignedBills] = useState([]);
    const [onTheWayBills, setOnTheWayBills] = useState([]);

    const [billData, setBillData] = useState(null);
    const [statusDialog, setStatusDialog] = useState(false);

    const handleOnclickItem = (data) => {
        printDebugLog("handleOnclickItem", data);
        navigate("/dashboard/billentry", {
            state: { tableProps: {}, sBillNo: data.billNo, billType: -1, isSearchByShowBills: true },
            replace: true
        });
    }

    const handleOnClickDeliveryItem = (data) => {
        printDebugLog("handleOnClickDeliveryItem", data);
        if (data !== null) {
            setStatusDialog(true);
        } else {
            setStatusDialog(false);
        }
        setBillData(data);
    }

    const fireSnackBarMsg = ({ msg, mHorizontal = "right", mVertical = "top", mColor = "#008b8b" }) => {
        showSnackbar(msg, mColor, mVertical, mHorizontal);
    };

    const renderBlock = (title, color, items) => (
        <div className="status-block" key={title}>
            <div className="status-header" style={{ borderLeftColor: color }}>
                <h3>{title}</h3>
            </div>

            <div className="status-scroll">
                {items.map((item) => (
                    <div key={item.id} className="bill-wrapper">
                        <BillListDisplay
                            data={item}
                            deliveryStatus={title}
                            quickBillAllowed={false}
                            onClickBill={handleOnclickItem}
                            cartType={storeActiveShopHash.cartType}
                            isOnlineBill={true}
                            onClickDeliverItem={handleOnClickDeliveryItem}
                        />
                    </div>
                ))}
            </div>
            {
                (!items || (items?.length ?? 0) === 0) &&
                <p style={{
                    whiteSpace: 'pre-wrap',
                    overflowWrap: 'break-word', fontSize: '16px',
                    justifyContent: 'center', alignItems: 'center',
                    alignContent: 'center', fontFamily: "monospace"
                }}>No item to display</p>
            }
        </div>
    );

    const dialogActionButton = () => {
        return (
            <>
                <SnpButton variant="contained" color="primary" onPointerDown={() => upateDeliveryStatus()}>
                    Update
                </SnpButton>
                <DismissButton variant="contained" color="primary" onPointerDown={() => handleOnClickDeliveryItem(null)}>
                    Do it later!
                </DismissButton>
            </>
        )
    }


    useEffect(() => {
        getBills();
    }, [storeActiveShopHash, storeApiUrl]);

    const updateBillData = (deliveryStatus, data = []) => {
        if (deliveryStatus === INITIATED) {
            setInitiatedBills(data ?? []);
        } else if (deliveryStatus === TRIP_ASSIGNED) {
            setAssignedBills(data ?? []);
        } else if (deliveryStatus === TRIP_RUNNING) {
            setOnTheWayBills(data ?? []);
        }
    }

    const getBills = async () => {
        getBillDetails(INITIATED);
        getBillDetails(TRIP_ASSIGNED);
        getBillDetails(TRIP_RUNNING);
    }

    const getBillDetails = async (deliveryStatus) => {
        try {
            setLoader(true);
            const data = {
                jcJson: {
                    apiToken: getAuthSession().apiToken,
                    shopHash: storeActiveShopHash.id,
                    status: deliveryStatus,
                }
            }
            const cartUrl = `${storeApiUrl.BACKEND_BASE_URL}mart-bill/get-bill-by-status`;
            const response = await snpAxios.post(cartUrl, data);
            if (response.data.status === SUCCESS) {
                const payLoad = response.data.jcJson;
                updateBillData(deliveryStatus, payLoad.data);
            } else {
                updateBillData(deliveryStatus);
            }
        } catch (error) {
            console.error("getBillDetails", deliveryStatus, error);
        } finally {
            setLoader(false);
        }
    };

    const upateDeliveryStatus = async () => {
        try {
            if (!billData) {
                fireSnackBarMsg({ msg: "No data to submit", mColor: "#7a0000" })
                return;
            }
            setLoader(true);
            const data = {
                jcJson: {
                    apiToken: getAuthSession().apiToken,
                    shopHash: storeActiveShopHash.id,
                    deliveryInformation: billData.deliveryInformation,
                    homeDelvId: billData.homeDelvId,
                    tripDetails: billData.tripDetails,
                    delvName: billData.delvName,
                    id: billData.id,
                }
            }
            const cartUrl = `${storeApiUrl.BACKEND_BASE_URL}mart-bill/update-delivery-status`;
            const response = await snpAxios.post(cartUrl, data);
            if (response.data.status === SUCCESS) {
                getBills();
            } else {
                setLoader(false);
            }
        } catch (error) {
            console.error("upateDeliveryStatus", error);
        } finally {
            handleOnClickDeliveryItem(null);
        }
    };

    return (
        <SnpBaseLayout
            loader={loader}
            snackbarConfig={{ ...snackbarConfig, onClose: closeSnackbar }}
            dialogConfig={dialogConfig}
        >
            <div className="status-board">
                <div>
                    <SnpButton variant="contained" color="primary" onPointerDown={() => navigate("/dashboard/showbills")}>
                        Show Bills
                    </SnpButton>
                </div>
                {renderBlock("Initiated", "#00bcd4", initiatedBills)}
                {renderBlock("Assigned", "#ff9800", assignedBills)}
                {renderBlock("On the Way", "#4caf50", onTheWayBills)}
                <DialogBox
                    showDialog={statusDialog}
                    title="Updare Delivery Information"
                    onClose={() => handleOnClickDeliveryItem(null)}
                    dialogContent={<DeliveryStatusChange
                        billData={billData}
                        setBillData={setBillData} />
                    }
                    dialogActions={dialogActionButton()}
                ></DialogBox>
            </div>

        </SnpBaseLayout>
    )
};

export default OnlineWatchBill;
